import React, { useState, useEffect, useCallback } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Database, History, RotateCcw, ShieldCheck, ShieldAlert, FileText } from "lucide-react";
import { fetchBackupJobs } from "./backupApi";
import { fetchRestoreJobs, validateBackupArchive, triggerRestoreJob } from "./restoreApi";
import type { BackupJob, RestoreJob, RestoreMode } from "./types";

export const RestoreCenterPage: React.FC = () => {
  const [completedBackups, setCompletedBackups] = useState<BackupJob[]>([]);
  const [restoreHistory, setRestoreHistory] = useState<RestoreJob[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedBackupId, setSelectedBackupId] = useState<string | null>(null);
  const [mode, setMode] = useState<RestoreMode>("Dry-Run");

  const [validating, setValidating] = useState<boolean>(false);
  const [validationResult, setValidationResult] = useState<{ success: boolean; message?: string; details?: any; error?: string } | null>(null);

  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [allowProduction, setAllowProduction] = useState<boolean>(false);

  const [executing, setExecuting] = useState<boolean>(false);
  const [executeResult, setExecuteResult] = useState<{ success: boolean; message: string; mode?: string; status?: string; error?: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [backups, restores] = await Promise.all([
        fetchBackupJobs(),
        fetchRestoreJobs(),
      ]);
      setCompletedBackups(backups.filter((b) => b.status === "Completed"));
      setRestoreHistory(restores);
    } catch (err: any) {
      console.error("RestoreCenterPage fetch error:", err);
      setError(err?.message || "Failed to load backup and restore data from API.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleValidate = async () => {
    if (!selectedBackupId) return;
    setValidating(true);
    setValidationResult(null);
    const res = await validateBackupArchive({ backup_id: selectedBackupId });
    setValidating(false);
    setValidationResult(res);
  };

  const handleExecuteRestore = async () => {
    if (!selectedBackupId) return;
    setExecuting(true);
    setExecuteResult(null);

    const res = await triggerRestoreJob({
      backup_id: selectedBackupId,
      mode,
      confirm_destructive_restore: confirmed,
      allow_production_restore: allowProduction,
    });

    setExecuting(false);
    setExecuteResult(res);
    if (res.success) {
      await loadData();
    }
  };

  const selectedJob = completedBackups.find((b) => b.id === selectedBackupId);

  return (
    <div className="space-y-4 text-xs font-sans">
      {/* Warning Banner */}
      <div className="flex items-start gap-3 p-4 bg-rose-950/40 border border-rose-800 rounded-xl">
        <ShieldAlert className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-rose-300 text-sm">⚠ Database Restoration & DR Validation Pipeline</p>
          <p className="text-[11px] text-rose-400/80 mt-1">
            Restoration alters system database state. All restore actions default to <strong className="text-amber-300">Dry-Run Simulation</strong> or <strong className="text-cyan-300">Validate-Only</strong>. Live restores require explicit double-confirmation.
          </p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-rose-950/80 border border-rose-800 rounded-xl text-rose-200 flex items-center justify-between">
          <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-rose-400" />{error}</span>
          <button onClick={loadData} className="px-2 py-1 bg-rose-800 text-white rounded text-[10px]">Retry</button>
        </div>
      )}

      {/* 1. Recovery Point Selection */}
      <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase">1. Select Recovery Point</h3>
          <span className="text-[10px] text-slate-500 font-mono">{completedBackups.length} points available</span>
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-500 font-mono">
            Loading recovery points from system backups...
          </div>
        ) : completedBackups.length === 0 ? (
          <div className="p-6 text-center text-slate-500 font-mono space-y-1">
            <Database className="w-6 h-6 text-slate-600 mx-auto mb-1" />
            <p className="font-bold text-slate-400">No Completed Recovery Points Available</p>
            <p className="text-[10px]">Execute a backup job first in Backup Center.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {completedBackups.map((job) => (
              <label
                key={job.id}
                className={`flex items-center gap-3 p-3.5 border rounded-xl cursor-pointer transition-all ${
                  selectedBackupId === job.id ? "bg-indigo-950/50 border-indigo-600" : "bg-slate-950 border-slate-800 hover:border-slate-700"
                }`}
              >
                <input
                  type="radio"
                  name="backup-point"
                  value={job.id}
                  checked={selectedBackupId === job.id}
                  onChange={() => {
                    setSelectedBackupId(job.id);
                    setValidationResult(null);
                    setExecuteResult(null);
                    setConfirmed(false);
                    setAllowProduction(false);
                  }}
                  className="text-indigo-600"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[9px] font-bold font-mono px-1.5 py-0.5 bg-amber-950 text-amber-300 border border-amber-800 rounded uppercase">{job.type}</span>
                    <span className="text-[9px] text-slate-500">{job.trigger}</span>
                    {job.storage_provider === "cloudflare_r2" && (
                      <span className="text-[9px] font-bold text-sky-300 bg-sky-950 border border-sky-800 px-1.5 py-0.5 rounded">R2 Offsite</span>
                    )}
                  </div>
                  <p className="text-[11px] font-bold font-mono text-slate-100">{job.completedAt || job.file_name}</p>
                  <p className="text-[10px] text-slate-500 font-mono mt-0.5 truncate">{job.location || job.file_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-bold text-slate-300 font-mono">{job.size}</p>
                  <p className="text-[10px] text-slate-500">{job.duration}</p>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* 2. Restoration Mode & Non-Destructive Validation */}
      {selectedJob && (
        <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-4">
          <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase">2. Restoration Mode & Archive Inspection</h3>

          <div className="grid grid-cols-3 gap-3">
            {[
              { id: "Dry-Run", name: "Dry-Run Simulation", desc: "Validates archive & simulates execution without database mutations", color: "border-indigo-600 bg-indigo-950/40 text-indigo-300" },
              { id: "Validate-Only", name: "Validate Archive Only", desc: "Non-destructive pg_restore --list header & TOC verification", color: "border-cyan-600 bg-cyan-950/40 text-cyan-300" },
              { id: "Live-Restore", name: "Live Destructive Restore", desc: "Restores database state (Requires double confirmation)", color: "border-rose-600 bg-rose-950/40 text-rose-300" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id as RestoreMode)}
                className={`p-3 border rounded-xl text-left transition-all ${
                  mode === m.id ? m.color : "bg-slate-950 border-slate-800 hover:border-slate-700 text-slate-400"
                }`}
              >
                <p className="font-bold text-xs">{m.name}</p>
                <p className="text-[10px] text-slate-500 mt-1">{m.desc}</p>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-800">
            <span className="text-[11px] text-slate-400 font-mono">
              Selected Archive: <strong className="text-slate-200">{selectedJob.file_name || selectedJob.id}</strong>
            </span>
            <button
              onClick={handleValidate}
              disabled={validating}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-cyan-700 hover:bg-cyan-600 disabled:opacity-50 text-white font-bold rounded-lg text-xs transition-colors"
            >
              <ShieldCheck className={`w-4 h-4 ${validating ? "animate-spin" : ""}`} />
              <span>{validating ? "Validating Archive..." : "Validate Archive (pg_restore --list)"}</span>
            </button>
          </div>

          {validationResult && (
            <div className={`p-3.5 rounded-xl border font-mono text-xs ${
              validationResult.success ? "bg-emerald-950/50 border-emerald-800 text-emerald-300" : "bg-rose-950/50 border-rose-800 text-rose-300"
            }`}>
              <div className="flex items-center gap-2 font-bold mb-1">
                {validationResult.success ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-rose-400" />}
                <span>{validationResult.message || validationResult.error}</span>
              </div>
              {validationResult.details && (
                <div className="text-[10px] text-slate-400 mt-2 space-y-0.5 bg-slate-950/80 p-2 rounded border border-slate-800">
                  <p>Inspector: <span className="text-slate-200">{validationResult.details.inspector || "—"}</span></p>
                  <p>File Size: <span className="text-slate-200">{validationResult.details.file_size_bytes || 0} bytes</span></p>
                  {validationResult.details.toc_entry_count && (
                    <p>TOC Table Entries: <span className="text-slate-200">{validationResult.details.toc_entry_count} entries</span></p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 3. Execution & Confirmation */}
      {selectedJob && (
        <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-3">
          <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase">3. Execute Restoration Pipeline</h3>

          {mode === "Live-Restore" ? (
            <div className="space-y-2.5 p-3.5 bg-rose-950/30 border border-rose-800/80 rounded-xl">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 text-rose-600"
                />
                <span className="text-[11px] text-slate-300">
                  I explicitly confirm that I want to execute a <strong className="text-rose-300">live destructive database restore</strong>. I understand database tables will be overwritten.
                </span>
              </label>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowProduction}
                  onChange={(e) => setAllowProduction(e.target.checked)}
                  className="mt-0.5 text-rose-600"
                />
                <span className="text-[11px] text-slate-300">
                  I confirm maintenance mode is active and allow production restore execution.
                </span>
              </label>
            </div>
          ) : (
            <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-400 font-mono text-[11px]">
              Mode is currently set to <strong className="text-amber-300">{mode}</strong>. No database tables will be mutated.
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={handleExecuteRestore}
              disabled={executing || (mode === "Live-Restore" && (!confirmed || !allowProduction))}
              className={`flex items-center gap-2 px-5 py-2.5 text-white font-bold rounded-xl text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                mode === "Live-Restore" ? "bg-rose-700 hover:bg-rose-600" : "bg-indigo-600 hover:bg-indigo-500"
              }`}
            >
              <RotateCcw className={`w-4 h-4 ${executing ? "animate-spin" : ""}`} />
              <span>{executing ? "Executing Restoration..." : `Run ${mode}`}</span>
            </button>
          </div>

          {executeResult && (
            <div className={`p-3.5 rounded-xl border font-mono text-xs ${
              executeResult.success ? "bg-emerald-950/50 border-emerald-800 text-emerald-300" : "bg-rose-950/50 border-rose-800 text-rose-300"
            }`}>
              <div className="flex items-center gap-2 font-bold">
                {executeResult.success ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-rose-400" />}
                <span>{executeResult.message || executeResult.error}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4. Restore Execution History */}
      <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-indigo-400" />
            <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase">Restoration Execution History ({restoreHistory.length})</h3>
          </div>
          <button onClick={loadData} className="text-[10px] text-indigo-400 hover:underline font-mono">Refresh Log</button>
        </div>

        {restoreHistory.length === 0 ? (
          <div className="p-4 text-center text-slate-500 font-mono">
            No database restore jobs have been recorded yet.
          </div>
        ) : (
          <div className="space-y-2 font-mono text-xs">
            {restoreHistory.map((res) => (
              <div key={res.id} className="p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-bold px-1.5 py-0.5 bg-slate-800 text-slate-300 rounded uppercase">{res.mode}</span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${
                      res.status === "Completed" ? "bg-emerald-950 text-emerald-300 border border-emerald-800" : "bg-rose-950 text-rose-300 border border-rose-800"
                    }`}>{res.status}</span>
                    <span className="text-slate-400 text-[10px]">{res.completedAt || res.started_at}</span>
                  </div>
                  <p className="text-[10px] text-slate-500">{res.backup_file_path}</p>
                </div>
                <div className="text-right">
                  <span className="text-slate-400 text-[10px]">{res.duration || "—"}</span>
                  <p className="text-[9px] text-slate-500">{res.performedBy || "Admin"}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Validation Reminder */}
      <div className="flex items-center gap-3 p-3.5 bg-amber-950/30 border border-amber-800/50 rounded-xl">
        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
        <p className="text-[11px] text-amber-300">
          Post-Restore Protocol: run <code className="font-mono text-amber-200">python manage.py migrate --check</code> and verify System Health Probes before disabling maintenance mode.
        </p>
      </div>
    </div>
  );
};
